import {
  CalendarDays,
  CheckCircle2,
  ClipboardPen,
  CreditCard,
  ExternalLink,
  FileText,
  History,
  Languages,
  Mail,
  MapPin,
  MessageCircle,
  NotebookText,
  Phone,
  Send,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { useEffect, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useErpData } from "../data/DataProvider";
import { formatDate, formatDateTime, formatMoney, serviceLabel } from "../data/format";
import type { Booking, Lead, Order, TimelineEvent } from "../data/types";
import { ServiceBadge } from "./ServiceBadge";
import { Button, Dialog, Field, InlineNotice, SelectField, StatusBadge, type Tone } from "./ui";

export const paymentStatusMap = {
  paid: { label: "Pagado", tone: "success" as Tone },
  pending: { label: "Pendiente", tone: "warning" as Tone },
  failed: { label: "Fallo de pago", tone: "danger" as Tone },
  refunded: { label: "Reembolsado", tone: "neutral" as Tone },
};

export const orderStatusMap = {
  new: { label: "Nuevo", tone: "info" as Tone },
  pending: { label: "Pendiente", tone: "warning" as Tone },
  confirmed: { label: "Confirmado", tone: "success" as Tone },
  cancelled: { label: "Cancelado", tone: "danger" as Tone },
  failed: { label: "Fallido", tone: "danger" as Tone },
};

export const bookingStatusMap = {
  holding: { label: "Bloqueo temporal", tone: "warning" as Tone },
  pending_payment: { label: "Pendiente de pago", tone: "warning" as Tone },
  confirmed: { label: "Confirmada", tone: "success" as Tone },
  completed: { label: "Atendida", tone: "info" as Tone },
  cancelled: { label: "Cancelada", tone: "danger" as Tone },
  failed: { label: "Fallida", tone: "danger" as Tone },
};

function passengerCountLabel(booking: Booking): string {
  const parts = [
    booking.adults ? `${booking.adults} adulto${booking.adults === 1 ? "" : "s"}` : "",
    booking.children ? `${booking.children} niño${booking.children === 1 ? "" : "s"}` : "",
    booking.infants ? `${booking.infants} menor${booking.infants === 1 ? "" : "es"}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : `${booking.guests} en total`;
}

export function Timeline({ events }: { events: TimelineEvent[] }) {
  return (
    <ol className="timeline">
      {events.map((event) => (
        <li key={event.id} className={`timeline__item timeline__item--${event.tone}`}>
          <span className="timeline__dot" aria-hidden="true" />
          <div>
            <strong>{event.title}</strong>
            <p>{event.description}</p>
            <small>{formatDateTime(event.occurredAt)}{event.actor ? ` · ${event.actor}` : ""}</small>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ContactActions({ phone, email }: { phone: string; email?: string }) {
  const normalizedPhone = phone.replace(/\D/g, "");
  return (
    <div className="contact-actions" aria-label="Acciones de contacto">
      <a className="button button--secondary" href={`tel:+${normalizedPhone}`}><Phone size={17} /> Llamar</a>
      <a className="button button--secondary" href={`https://wa.me/${normalizedPhone}`} target="_blank" rel="noreferrer"><MessageCircle size={17} /> WhatsApp</a>
      {email ? <a className="button button--secondary" href={`mailto:${email}`}><Mail size={17} /> Correo</a> : null}
    </div>
  );
}

interface DetailTabDefinition<T extends string> {
  id: T;
  label: string;
  icon: typeof FileText;
}

function DetailTabs<T extends string>({ label, prefix, panelId, sections, activeSection, onChange }: {
  label: string;
  prefix: string;
  panelId: string;
  sections: DetailTabDefinition<T>[];
  activeSection: T;
  onChange: (section: T) => void;
}) {
  const move = (event: ReactKeyboardEvent<HTMLButtonElement>, current: T) => {
    const currentIndex = sections.findIndex((section) => section.id === current);
    let nextIndex = currentIndex;
    if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (currentIndex + 1) % sections.length;
    else if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (currentIndex - 1 + sections.length) % sections.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = sections.length - 1;
    else return;
    event.preventDefault();
    const next = sections[nextIndex];
    if (!next) return;
    onChange(next.id);
    document.getElementById(`${prefix}-tab-${next.id}`)?.focus();
  };

  return (
    <nav className="detail-dialog__nav" role="tablist" aria-label={label}>
      {sections.map((section) => {
        const Icon = section.icon;
        const selected = activeSection === section.id;
        return (
          <button
            key={section.id}
            id={`${prefix}-tab-${section.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(section.id)}
            onKeyDown={(event) => move(event, section.id)}
          >
            <Icon size={20} aria-hidden="true" />
            <span>{section.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function OrderDialog({ order, onClose }: { order: Order | null; onClose: () => void }) {
  if (!order) return null;
  return (
    <Dialog
      open
      size="executive"
      title={order.number}
      eyebrow="Detalle del pedido"
      onClose={onClose}
      footer={(
        <div className="detail-dialog__footer-actions">
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          <a className="button button--secondary" href={order.sourceUrl} target="_blank" rel="noreferrer"><ExternalLink size={18} /> Abrir pedido en WooCommerce</a>
        </div>
      )}
    >
      <OrderDetailContent order={order} onNavigate={onClose} />
    </Dialog>
  );
}

function OrderDetailContent({ order, onNavigate }: { order: Order; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canSeeMoney = hasPermission("finance.read");
  const payment = paymentStatusMap[order.paymentStatus];
  const status = orderStatusMap[order.status];
  const [activeSection, setActiveSection] = useState<"summary" | "payment" | "timeline">("summary");
  useEffect(() => setActiveSection("summary"), [order.id]);
  const sections = [
    { id: "summary" as const, label: "Resumen", icon: FileText },
    ...(canSeeMoney ? [{ id: "payment" as const, label: "Pago", icon: CreditCard }] : []),
    { id: "timeline" as const, label: "Cronología", icon: History },
  ];
  return (
    <div className="detail-dialog">
      <div className="detail-dialog__summary">
        <div className="detail-status-row">
          <StatusBadge tone={payment.tone}>{payment.label}</StatusBadge>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </div>
        <section className="detail-hero">
          <div>
            <span>Cliente</span>
            <h3>{order.customer}</h3>
            <p>{order.experience}</p>
          </div>
          {canSeeMoney ? <strong>{formatMoney(order.total)} <small>{order.currency}</small></strong> : <span className="financially-hidden">Importe restringido</span>}
        </section>
        <ContactActions phone={order.phone} email={order.email} />
      </div>

      <div className="detail-dialog__workspace">
        <DetailTabs label="Secciones del pedido" prefix="order" panelId="order-tabpanel" sections={sections} activeSection={activeSection} onChange={setActiveSection} />
        <div className="detail-dialog__content" id="order-tabpanel" role="tabpanel" aria-labelledby={`order-tab-${activeSection}`}>
          {activeSection === "summary" ? (
            <>
              <section className="detail-section detail-section--flush">
                <h3>Información</h3>
                <dl className="detail-list">
                  <div><dt>Experiencia</dt><dd>{order.experience}</dd></div>
                  <div><dt>Fecha y horario</dt><dd>{formatDate(order.visitDate)} · {order.time}</dd></div>
                  <div><dt>Servicio</dt><dd>{serviceLabel(order.service)}</dd></div>
                  <div><dt>Método de pago</dt><dd>{order.paymentMethod}</dd></div>
                  {canSeeMoney ? <div><dt>Transacción</dt><dd>{order.transactionId ?? "Sin transacción confirmada"}</dd></div> : null}
                  <div><dt>Responsable</dt><dd>{order.assignedTo ?? "Sin asignar"}</dd></div>
                </dl>
              </section>
              {order.bookingId ? (
                <button className="linked-record" type="button" onClick={() => { navigate(`/reservas?reference=${order.bookingId}`); onNavigate?.(); }}>
                  <CalendarDays aria-hidden="true" />
                  <span><small>Reserva vinculada</small><strong>{order.bookingId}</strong></span>
                  <ExternalLink size={17} aria-hidden="true" />
                </button>
              ) : null}
            </>
          ) : null}

          {activeSection === "payment" && canSeeMoney ? (
            <>
              <section className="detail-section detail-section--flush">
                <h3>Artículos</h3>
                <div className="line-items">
                  {order.items.map((item) => (
                    <div key={item.id}>
                      <span><strong>{item.name}</strong><small>{item.quantity} × {formatMoney(item.unitPrice)}</small></span>
                      <strong>{formatMoney(item.total)}</strong>
                    </div>
                  ))}
                  <div className="line-items__total"><span>Total</span><strong>{formatMoney(order.total)} {order.currency}</strong></div>
                </div>
              </section>
              <p className="authority-note">Cambios de precio, reembolsos y cancelaciones se realizan únicamente en WooCommerce.</p>
            </>
          ) : null}

          {activeSection === "timeline" ? <section className="detail-section detail-section--flush"><h3>Cronología</h3><Timeline events={order.timeline} /></section> : null}
        </div>
      </div>
    </div>
  );
}

export function BookingDialog({ booking, onClose }: { booking: Booking | null; onClose: () => void }) {
  const { hasPermission } = useAuth();
  const { snapshot, executeCommand } = useErpData();
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState(booking?.assignedAgentSourceId ?? "");
  const [language, setLanguage] = useState<"es" | "en">(booking?.language ?? "es");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState("");
  const [activeSection, setActiveSection] = useState<"summary" | "passengers" | "payment" | "notes" | "timeline">("summary");
  useEffect(() => {
    setAssignee(booking?.assignedAgentSourceId ?? "");
    setLanguage(booking?.language ?? "es");
    setActiveSection("summary");
  }, [booking?.assignedAgentSourceId, booking?.id, booking?.language]);
  if (!booking) return null;
  const status = bookingStatusMap[booking.status];
  const canSeeMoney = hasPermission("finance.read");
  const canAttend = hasPermission("bookings.attend") && booking.paid && booking.status === "confirmed" && !booking.attended;
  const bookingSections = [
    { id: "summary" as const, label: "Resumen", icon: FileText },
    { id: "passengers" as const, label: "Pasajeros", icon: Users },
    { id: "payment" as const, label: "Pago", icon: CreditCard },
    ...(hasPermission("bookings.note") ? [{ id: "notes" as const, label: "Notas", icon: NotebookText }] : []),
    { id: "timeline" as const, label: "Cronología", icon: History },
  ];

  const run = async (label: string, request: Parameters<typeof executeCommand>[0]) => {
    setWorking(label);
    setFeedback("");
    try {
      await executeCommand(request);
      setFeedback("Cambio guardado y enviado a WordPress.");
      if (request.type === "booking.add_note") setNote("");
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No fue posible guardar el cambio.");
    } finally {
      setWorking("");
    }
  };

  const submitNote = (event: FormEvent) => {
    event.preventDefault();
    if (!note.trim()) return;
    void run("note", { type: "booking.add_note", aggregateId: booking.id, expectedVersion: booking.version, payload: { note: note.trim() } });
  };

  return (
    <Dialog
      open
      size="executive"
      title={booking.reference}
      eyebrow="Ficha de reserva"
      onClose={onClose}
      footer={(
        <div className="detail-dialog__footer-actions">
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          <a className="button button--secondary" href={`https://wa.me/${booking.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Abrir WhatsApp</a>
        </div>
      )}
    >
      <div className="detail-dialog">
        <div className="detail-dialog__summary">
          <div className="detail-status-row">
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            <StatusBadge tone={booking.paid ? "success" : "warning"}>{booking.paid ? "Pagada" : "Sin pago"}</StatusBadge>
            {booking.attended ? <StatusBadge tone="info">Atendida</StatusBadge> : null}
            <StatusBadge tone="info">{booking.language.toUpperCase()}</StatusBadge>
          </div>
          <section className="detail-hero">
            <div><span>Cliente</span><h3>{booking.customer}</h3><p>{booking.experience}</p></div>
            <strong>{booking.guests} <small>personas</small></strong>
          </section>
          <ContactActions phone={booking.phone} email={booking.email} />
        </div>

        <div className="detail-dialog__workspace">
          <DetailTabs label="Secciones de la reserva" prefix="booking" panelId="booking-tabpanel" sections={bookingSections} activeSection={activeSection} onChange={setActiveSection} />

          <div className="detail-dialog__content" id="booking-tabpanel" role="tabpanel" aria-labelledby={`booking-tab-${activeSection}`}>
            {feedback ? <InlineNotice tone={feedback.startsWith("Cambio") ? "success" : "danger"}>{feedback}</InlineNotice> : null}

            {activeSection === "summary" ? (
              <>
                <section className="booking-facts" aria-label="Datos de la reserva">
                  <div><CalendarDays /><span><small>Fecha</small><strong>{formatDate(booking.date)} · {booking.time}</strong></span></div>
                  <div><Users /><span><small>Personas</small><strong>{passengerCountLabel(booking)}</strong></span></div>
                  <div><MapPin /><span><small>Punto de encuentro</small><strong>{booking.location}</strong></span></div>
                  {booking.orderNumber ? <div><ClipboardPen /><span><small>Pedido</small><strong>{booking.orderNumber}</strong></span></div> : null}
                  <div><Users /><span><small>Responsable</small><strong>{booking.assignedTo ?? "Sin asignar"}</strong></span></div>
                  <div><Languages /><span><small>Idioma de confirmación</small><strong>{booking.language === "en" ? "English" : "Español"}</strong></span></div>
                </section>

                {hasPermission("bookings.language") ? (
                  <section className="detail-section detail-command">
                    <h3>Idioma de la confirmación</h3>
                    <p className="muted-copy">Se detecta al iniciar la reserva. Corrígelo aquí antes de reenviar el documento.</p>
                    <div className="inline-command">
                      <SelectField label="Idioma" value={language} onChange={(event) => setLanguage(event.target.value as "es" | "en")}>
                        <option value="es">Español</option>
                        <option value="en">English</option>
                      </SelectField>
                      <Button variant="secondary" disabled={working === "language" || language === booking.language} onClick={() => void run("language", { type: "booking.set_language", aggregateId: booking.id, expectedVersion: booking.version, payload: { language } })}>Guardar idioma</Button>
                    </div>
                  </section>
                ) : null}

                {hasPermission("bookings.assign") ? (
                  <section className="detail-section detail-command">
                    <h3>Asignar responsable</h3>
                    <div className="inline-command">
                      <SelectField label="Operador" value={assignee} onChange={(event) => setAssignee(event.target.value)}>
                        <option value="">Sin asignar</option>
                        {snapshot?.team.filter((member) => member.status === "active" && member.agentSourceId && ["operator", "manager"].includes(member.role)).map((member) => <option key={member.id} value={member.agentSourceId}>{member.name}</option>)}
                      </SelectField>
                      <Button variant="secondary" disabled={working === "assign" || !assignee || assignee === booking.assignedAgentSourceId} onClick={() => void run("assign", { type: "booking.assign_agent", aggregateId: booking.id, expectedVersion: booking.version, payload: { agent_source_id: assignee } })}>Asignar</Button>
                    </div>
                  </section>
                ) : null}

                {hasPermission("bookings.attend") ? (
                  <section className="attendance-command">
                    <div><UserRoundCheck aria-hidden="true" /><span><strong>Asistencia</strong><small>{booking.attended ? "El cliente ya fue atendido." : booking.paid && booking.status === "confirmed" ? "La reserva cumple las condiciones." : "Requiere pago y confirmación antes de marcarla."}</small></span></div>
                    <Button disabled={!canAttend || working === "attend"} onClick={() => void run("attend", { type: "booking.mark_attended", aggregateId: booking.id, expectedVersion: booking.version, payload: {} })} icon={CheckCircle2}>
                      {booking.attended ? "Atendida" : "Marcar atendida"}
                    </Button>
                  </section>
                ) : null}
              </>
            ) : null}

            {activeSection === "passengers" ? (
              <section className="detail-section detail-section--flush">
                <h3>Pasajeros y peso</h3>
                {booking.passengers.length ? (
                  <div className="passenger-list">
                    {booking.passengers.map((passenger) => {
                      const weight = [passenger.weightBand, passenger.weightKg === undefined ? "" : `${passenger.weightKg} kg`].filter(Boolean).join(" · ");
                      const passengerType = passenger.type === "adult" ? "Adulto" : passenger.type === "child" ? "Niño" : "Menor";
                      const passengerAge = passenger.age ? `${passenger.age} ${passenger.age === 1 ? "año" : "años"}` : "Edad no registrada";
                      return (
                        <div key={passenger.id}>
                          <span><strong>{passenger.name}</strong><small>{passengerType} · {passengerAge}</small></span>
                          <span className="passenger-weight"><strong>{weight || (booking.service === "balloon" ? "Peso no registrado" : "No aplica")}</strong>{canSeeMoney && passenger.weightFee ? <small>+{formatMoney(passenger.weightFee)} {booking.currency}</small> : null}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="muted-copy">No hay pasajeros registrados para esta reserva.</p>}
              </section>
            ) : null}

            {activeSection === "payment" ? (
              <>
                <div className="payment-authority-strip">
                  <span><small>Estado</small><strong>{booking.paid ? "Pago confirmado" : "Sin pago confirmado"}</strong></span>
                  <span><small>Autoridad</small><strong>WooCommerce / WordPress</strong></span>
                </div>
                {booking.addons.length ? (
                  <section className="detail-section">
                    <h3>Complementos</h3>
                    <div className="line-items">
                      {booking.addons.map((addon, index) => <div key={`${addon.key}-${index}`}><span><strong>{addon.name}</strong><small>{addon.quantity} {addon.perPerson ? "persona(s)" : "unidad(es)"}{addon.unitPrice === null ? "" : ` · ${formatMoney(addon.unitPrice)} c/u`}</small></span>{addon.subtotal === null || !canSeeMoney ? null : <strong>{formatMoney(addon.subtotal)} {addon.currency || booking.currency}</strong>}</div>)}
                    </div>
                  </section>
                ) : null}
                {canSeeMoney && booking.total !== null ? (
                  <section className="detail-section">
                    <h3>Desglose de la reserva</h3>
                    <dl className="booking-totals">
                      <div><dt>Tarifa</dt><dd>{formatMoney(booking.baseTotal ?? 0)} {booking.currency}</dd></div>
                      {(booking.weightTotal ?? 0) > 0 ? <div><dt>Cargos por peso</dt><dd>{formatMoney(booking.weightTotal ?? 0)} {booking.currency}</dd></div> : null}
                      {(booking.addonsTotal ?? 0) > 0 ? <div><dt>Complementos</dt><dd>{formatMoney(booking.addonsTotal ?? 0)} {booking.currency}</dd></div> : null}
                      {(booking.taxTotal ?? 0) > 0 ? <div><dt>Impuestos</dt><dd>{formatMoney(booking.taxTotal ?? 0)} {booking.currency}</dd></div> : null}
                      <div className="booking-totals__total"><dt>Total</dt><dd>{formatMoney(booking.total)} {booking.currency}</dd></div>
                    </dl>
                  </section>
                ) : <p className="financially-hidden">Los importes están restringidos para este perfil.</p>}
              </>
            ) : null}

            {activeSection === "notes" && hasPermission("bookings.note") ? (
              <section className="detail-section detail-command detail-section--flush">
                <h3>Notas operativas</h3>
                {booking.notes.length ? <div className="notes-list">{booking.notes.map((item) => <div key={item.id}><p>{item.body}</p><small>{item.author} · {formatDateTime(item.createdAt)}</small></div>)}</div> : <p className="muted-copy">Aún no hay notas para esta reserva.</p>}
                <form className="note-form" onSubmit={submitNote}>
                  <label><span className="sr-only">Nueva nota</span><textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Escribe una nota breve para el equipo..." maxLength={1000} /></label>
                  <Button type="submit" icon={ClipboardPen} disabled={working === "note" || !note.trim()}>Guardar nota</Button>
                </form>
              </section>
            ) : null}

            {activeSection === "timeline" ? <section className="detail-section detail-section--flush"><h3>Cronología</h3><Timeline events={booking.timeline} /></section> : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export function LeadDialog({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  const { hasPermission } = useAuth();
  const { executeCommand } = useErpData();
  const [feedback, setFeedback] = useState("");
  const [working, setWorking] = useState(false);
  const [activeSection, setActiveSection] = useState<"summary" | "followup">("summary");
  useEffect(() => setActiveSection("summary"), [lead?.id]);
  if (!lead) return null;
  const tone: Tone = lead.status === "new" ? "info" : lead.status === "qualified" ? "success" : lead.status === "lost" ? "danger" : "warning";
  const statusLabel = { new: "Pendiente", contacted: "En seguimiento", qualified: "Calificada", lost: "No convertida" }[lead.status];

  const markFollowup = async () => {
    setWorking(true);
    try {
      await executeCommand({ type: "lead.mark_followup", aggregateId: lead.id, expectedVersion: lead.version, payload: { status: "contacted", follow_up_at: new Date().toISOString() } });
      setFeedback("Seguimiento registrado.");
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No pudimos registrar el seguimiento.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog
      open
      size="executive"
      title={lead.customer}
      eyebrow="Ficha de solicitud"
      onClose={onClose}
      footer={(
        <div className="detail-dialog__footer-actions">
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          {hasPermission("leads.followup") ? <Button icon={Send} disabled={working || lead.status === "contacted"} onClick={() => void markFollowup()}>{lead.status === "contacted" ? "Seguimiento registrado" : "Registrar seguimiento"}</Button> : null}
        </div>
      )}
    >
      <div className="detail-dialog">
        <div className="detail-dialog__summary">
          <div className="detail-status-row"><StatusBadge tone={tone}>{statusLabel}</StatusBadge><ServiceBadge service={lead.service} tentative /></div>
          <section className="detail-hero"><div><span>Solicitud</span><h3>{lead.interest}</h3><p>Registrada {formatDateTime(lead.createdAt)}</p></div></section>
          <ContactActions phone={lead.phone} email={lead.email} />
        </div>
        <div className="detail-dialog__workspace">
          <DetailTabs
            label="Secciones de la solicitud"
            prefix="lead"
            panelId="lead-tabpanel"
            sections={[
              { id: "summary", label: "Resumen", icon: FileText },
              { id: "followup", label: "Seguimiento", icon: NotebookText },
            ]}
            activeSection={activeSection}
            onChange={setActiveSection}
          />
          <div className="detail-dialog__content" id="lead-tabpanel" role="tabpanel" aria-labelledby={`lead-tab-${activeSection}`}>
            {feedback ? <InlineNotice tone={feedback.startsWith("Seguimiento") ? "success" : "danger"}>{feedback}</InlineNotice> : null}
            {activeSection === "summary" ? (
              <section className="detail-section detail-section--flush">
                <h3>Datos solicitados</h3>
                <dl className="detail-list">
                  <div><dt>Contacto</dt><dd>{lead.customer}</dd></div>
                  <div><dt>Correo</dt><dd>{lead.email || "Sin correo"}</dd></div>
                  <div><dt>Teléfono</dt><dd>{lead.phone || "Sin teléfono"}</dd></div>
                  <div><dt>Fecha tentativa</dt><dd>{lead.requestedDate ? formatDate(lead.requestedDate) : "Sin fecha"}</dd></div>
                  <div><dt>Personas</dt><dd>{lead.peopleCount === undefined ? "Sin definir" : `${lead.peopleCount} personas`}</dd></div>
                  <div><dt>Monto estimado</dt><dd>{lead.estimatedTotal === undefined ? "Por cotizar" : `${formatMoney(lead.estimatedTotal)} ${lead.currency}`}</dd></div>
                </dl>
              </section>
            ) : null}
            {activeSection === "followup" ? (
              <section className="detail-section detail-section--flush">
                <h3>Seguimiento</h3>
                <dl className="detail-list">
                  <div><dt>Responsable</dt><dd>{lead.assignedTo ?? "Sin asignar"}</dd></div>
                  <div><dt>Próximo contacto</dt><dd>{lead.nextFollowupAt ? formatDateTime(lead.nextFollowupAt) : "Sin programar"}</dd></div>
                </dl>
                {lead.notes.length ? <div className="notes-list">{lead.notes.map((note) => <div key={note}><p>{note}</p></div>)}</div> : <p className="muted-copy">Aún no hay notas de seguimiento.</p>}
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
